import { useLanguage } from '@/context/LanguageContext';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, Globe, Cpu, Droplets, TrendingUp, ArrowRight, ExternalLink } from 'lucide-react';

export default function BusinessOpportunities() {
  const { t } = useLanguage();

  const opportunities = [
    { icon: Droplets, title: t('opportunities.opp_energy'), desc: t('opportunities.opp_energy_desc') },
    { icon: Building2, title: t('opportunities.opp_construction'), desc: t('opportunities.opp_construction_desc') },
    { icon: Globe, title: t('opportunities.opp_agriculture'), desc: t('opportunities.opp_agriculture_desc') },
    { icon: Cpu, title: t('opportunities.opp_technology'), desc: t('opportunities.opp_technology_desc') },
    { icon: TrendingUp, title: t('opportunities.opp_finance'), desc: t('opportunities.opp_finance_desc') },
    { icon: Briefcase, title: t('opportunities.opp_services'), desc: t('opportunities.opp_services_desc') },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="opportunities-page">
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white border-b-4 border-[#FFD700]">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <p className="text-xs text-[#FFD700] font-semibold uppercase tracking-[0.2em] mb-3">{t('opportunities.overline')}</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-serif">{t('opportunities.title')}</h1>
          <p className="text-gray-400 mt-3 max-w-2xl">{t('opportunities.subtitle')}</p>
        </div>
      </div>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif mb-6">{t('opportunities.intro_title')}</h2>
            <p className="text-gray-600 leading-relaxed">{t('opportunities.intro_desc')}</p>
          </div>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif">{t('opportunities.sectors_title')}</h2>
            <div className="w-16 h-1 bg-[#CC0000] mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white p-6 border border-gray-100 rounded-md hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#CC0000]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Useful Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight font-serif mb-8">{t('opportunities.links_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: 'AIPEX - Agencia de Investimento Privado e Promocao de Exportacoes', url: 'https://aipex.gov.ao' },
              { name: 'Banco Nacional de Angola', url: 'https://www.bna.ao' },
              { name: 'Camara de Comercio e Industria Angola-Espanha', url: '#' },
              { name: 'MIREX - Ministerio das Relacoes Exteriores', url: 'https://mirex.gov.ao' },
            ].map(link => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 border border-gray-200 rounded-md hover:border-[#CC0000]/30 hover:shadow-sm transition-all group">
                <span className="text-sm font-medium text-gray-700 group-hover:text-[#CC0000] transition-colors">{link.name}</span>
                <ExternalLink size={14} className="text-gray-400 group-hover:text-[#CC0000] flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white font-serif mb-4">{t('opportunities.cta_title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('opportunities.cta_desc')}</p>
          <Link to="/contactos" className="inline-flex items-center gap-2 px-6 py-3 bg-[#CC0000] text-white font-medium rounded-sm hover:bg-[#A30000] transition-colors" data-testid="opp-contact-btn">
            {t('opportunities.cta_contact')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
